import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import deliveryService from '../../../services/deliveryService';
import MessageModal from '../../shared/MessageModal';
import DeliveryRequesterSelectorModal from './DeliveryRequesterSelectorModal';

const { SectionTitle, Button } = CrudStyles;

const RequesterSection = styled.section`
    border-radius: 12px;
    padding: 24px;
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: ${({ theme }) =>
        theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(59, 130, 246, 0.04)'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid
        ${({ theme }) =>
            theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.12)'};
`;

const SectionTitleSoft = styled(SectionTitle)`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.gray?.[800] || '#374151'};
    margin-bottom: 0;
    margin-top: 0;
`;

const HelpText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[600] || '#6b7280'};
    max-width: 42rem;
`;

const HintError = styled.span`
    color: #b91c1c;
    font-size: 13px;
`;

const RequesterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
`;

const RequesterCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    min-width: 0;
`;

const RequesterCellLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
`;

const RequesterCellValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    word-break: break-word;
`;

const RequesterEmptyState = styled.div`
    padding: 18px 16px;
    text-align: center;
    border-radius: 10px;
    border: 1px dashed #cbd5e1;
    background: rgba(255, 255, 255, 0.7);
    color: #94a3b8;
    font-size: 14px;
    font-weight: 500;
`;

const RequesterActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
`;

export default function DeliveryRequesterSection({
    deliveryId,
    customerId,
    isTransportCompany = false,
    requesterId = null,
    requesterName = '',
    requesterEmail = '',
    onRequesterChange,
}) {
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [modalOpen, setModalOpen] = useState(false);
    const [patching, setPatching] = useState(false);

    const customerMissing = useMemo(() => {
        if (!isTransportCompany) return false;
        return customerId == null || String(customerId).trim() === '';
    }, [isTransportCompany, customerId]);

    const hasRequester = requesterId != null && !Number.isNaN(Number(requesterId));

    const openModal = () => {
        if (!deliveryId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'A entrega precisa estar criada antes de alterar o solicitante.',
            });
            return;
        }
        if (customerMissing) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message:
                    'Não foi possível identificar o cliente desta entrega (customerId). Abra o detalhe a partir da listagem ou garanta que a entrega retorne o identificador do cliente.',
            });
            return;
        }
        setModalOpen(true);
    };

    const handleSelectRequester = async (employee) => {
        if (employee?.id == null) return;
        if (customerMissing || !deliveryId) return;
        setPatching(true);
        try {
            await deliveryService.updateDeliveryRequester(
                deliveryId,
                employee.id,
                customerId,
                isTransportCompany,
            );
            onRequesterChange?.({
                id: employee.id,
                name: employee.name ?? '',
                email: employee.email ?? '',
                addressId: employee.addressId ?? null,
            });
            setModalOpen(false);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Não foi possível atualizar o solicitante da entrega.',
            });
        } finally {
            setPatching(false);
        }
    };

    const buttonLabel = hasRequester ? 'Selecionar/alterar solicitante' : 'Selecionar solicitante';

    return (
        <>
            <RequesterSection aria-labelledby="delivery-requester-title">
                <div>
                    <SectionTitleSoft id="delivery-requester-title">Solicitante</SectionTitleSoft>
                    <HelpText>
                        Use o botão abaixo para abrir a lista paginada de solicitantes. A seleção é salva ao escolher
                        um na lista. Se o novo solicitante tiver centro de custo, ele passa a valer para esta entrega.
                    </HelpText>
                </div>

                {customerMissing ? (
                    <HintError>
                        É necessário o customerId da entrega para usar esta função. Recarregue a página ou abra esta
                        entrega a partir da listagem (com empresa identificada).
                    </HintError>
                ) : null}

                {hasRequester ? (
                    <RequesterGrid>
                        <RequesterCell>
                            <RequesterCellLabel>ID</RequesterCellLabel>
                            <RequesterCellValue>{String(requesterId)}</RequesterCellValue>
                        </RequesterCell>
                        <RequesterCell>
                            <RequesterCellLabel>Nome</RequesterCellLabel>
                            <RequesterCellValue>{requesterName?.trim() || '—'}</RequesterCellValue>
                        </RequesterCell>
                        <RequesterCell>
                            <RequesterCellLabel>Email</RequesterCellLabel>
                            <RequesterCellValue>{requesterEmail?.trim() || '—'}</RequesterCellValue>
                        </RequesterCell>
                    </RequesterGrid>
                ) : (
                    <RequesterEmptyState>Nenhum solicitante atribuído</RequesterEmptyState>
                )}

                <RequesterActions>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={patching || !deliveryId || customerMissing}
                        onClick={openModal}
                        title="Abrir lista de solicitantes"
                    >
                        {patching ? 'Salvando…' : buttonLabel}
                    </Button>
                </RequesterActions>
            </RequesterSection>

            <DeliveryRequesterSelectorModal
                open={modalOpen}
                customerId={customerId}
                includeCustomerId={isTransportCompany}
                currentRequesterId={requesterId}
                onClose={() => !patching && setModalOpen(false)}
                onSelectRequester={handleSelectRequester}
                isSubmitting={patching}
            />

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </>
    );
}
